import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import InputField from "components/Input/InputField";
import { currency } from "const/constants";

const money = (value) => `${currency}${Number(value || 0).toFixed(2)}`;

/**
 * Edit account-row money fields for a linked module entry.
 * Saves via the accounts update path (parent apply + autosave) — not module-charges.
 *
 * "Agent cost" is what Kothar pays out on this record, the figure the Accounts list totals as
 * Agent Cost. It is NOT the client-facing charge: Agreed is derived from the module record's own
 * charge breakdown, so it stays read-only here and is edited on the record itself.
 */
const EditModuleChargesModal = ({ open, onClose, accountRow = null, onSaved }) => {
  const [values, setValues] = useState({
    cost: "",
    discount: "",
    agreed: "",
    referral: "",
  });

  useEffect(() => {
    if (!open) return;
    setValues({
      cost: accountRow?.cost ?? "",
      discount: accountRow?.discount ?? "",
      agreed: accountRow?.agreed ?? "",
      referral: accountRow?.referral ?? "",
    });
    // Only hydrate when opening so typing is not reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountRow?.uuid, accountRow?.id]);

  const handleChange = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = () => {
    onSaved?.({
      cost: values.cost,
      discount: values.discount,
      agreed: values.agreed,
      referral: values.referral,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Edit account figures</h3>
          <IconButton onClick={onClose} aria-label="Close">
            <IoClose className="text-primary" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-app-border bg-app-surface-muted/40 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
                Agreed
              </p>
              <p className="text-sm font-semibold text-app-text m-0 mt-0.5">
                {money(accountRow?.agreedAmount)}
              </p>
            </div>
            <div className="rounded-lg border border-app-border bg-app-surface-muted/40 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
                Paid
              </p>
              <p className="text-sm font-semibold text-app-text m-0 mt-0.5">
                {money(accountRow?.paidAmount)}
              </p>
            </div>
          </div>

          <InputField
            label="Agent cost"
            placeholder="0.00"
            type="number"
            value={values.cost}
            helperText="What Kothar pays on this record. Agreed comes from the record's own charges."
            onChange={handleChange("cost")}
          />
          <InputField
            label="Discount"
            placeholder="0.00"
            type="number"
            value={values.discount}
            onChange={handleChange("discount")}
          />
          <InputField
            label="MARA"
            placeholder="0.00"
            type="number"
            value={values.agreed}
            onChange={handleChange("agreed")}
          />
          <InputField
            label="Referral"
            placeholder="0.00"
            type="number"
            value={values.referral}
            onChange={handleChange("referral")}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModuleChargesModal;
